'use client';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { isGradientColorScheme } from '@/utils/gradientDirection';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import {
    getReviewSourceAlt,
    normalizeReviewSourceIcon,
} from '@/utils/reviewSourceAlt';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

const PACK_TOKENS = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
};

const normalizeStyleForPack = (style, pack) => {
    if (pack === 'brands') return 'brands';
    const allowed = ['solid', 'regular', 'light', 'thin'];
    return allowed.includes(style) ? style : 'solid';
};

const buildIconClass = (name, pack, style) => {
    const tokens = [];
    const normalizedPack = pack || 'classic';
    const normalizedStyle = normalizeStyleForPack(style, normalizedPack);
    (PACK_TOKENS[normalizedPack] || []).forEach((t) => tokens.push(t));
    if (normalizedPack !== 'brands') tokens.push(`fa-${normalizedStyle}`);
    tokens.push(`fa-${name}`);
    const seen = new Set();
    return tokens
        .filter((t) => {
            if (!t || seen.has(t)) return false;
            seen.add(t);
            return true;
        })
        .join(' ');
};

const FEATURE_ICON_SIZE_CLASSES = {
    default: '',
    'x-small': 'text-2xl sm:text-3xl lg:text-4xl',
    small: 'text-3xl sm:text-4xl lg:text-5xl',
    medium: 'text-4xl sm:text-5xl lg:text-6xl',
    large: 'text-5xl sm:text-6xl lg:text-7xl',
    'x-large': 'text-6xl sm:text-7xl lg:text-8xl',
};

const getFeatureIconSizeClass = (value) =>
    FEATURE_ICON_SIZE_CLASSES[
        String(value || 'default')
            .toLowerCase()
            .trim()
    ] || '';

const valOf = (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'object' && value && 'value' in value) {
        return value.value ?? fallback;
    }
    return value;
};

const hasText = (value) => {
    const normalized = valOf(value, '');
    if (value == null) return false;
    return (
        String(normalized)
            .replace(/<[^>]*>/g, '')
            .trim().length > 0
    );
};

const useSiteIconOverride = () => {
    const [pack, setPack] = useState('classic');
    const [style, setStyle] = useState('solid');

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        const read = () => {
            const nextPack = root.dataset.faPackOverride || 'classic';
            const nextStyle =
                root.dataset.faStyleOverride &&
                ['solid', 'regular', 'light', 'thin'].includes(
                    root.dataset.faStyleOverride
                )
                    ? root.dataset.faStyleOverride
                    : 'solid';
            setPack(nextPack);
            setStyle(nextStyle);
        };
        read();
        const observer = new MutationObserver(() => read());
        observer.observe(root, {
            attributes: true,
            attributeFilter: [
                'data-fa-pack-override',
                'data-fa-style-override',
            ],
        });
        return () => observer.disconnect();
    }, []);

    return { pack, style };
};

const normalizeAnimation = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
};

const mapGroup = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['ctas', 'cta', 'buttons', 'ctas-only', 'cta-only'].includes(v))
        return 'ctas';
    if (['features', 'feature', 'features-only', 'feature-only'].includes(v))
        return 'features';
    return 'content';
};

const getElementVariants = (preset, duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
};

const getStaggerContainer = (duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
};

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

export default function ReviewHero(props) {
    const {
        Header,
        Reviews,
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        OutlineButtonOpenInNewTab,
        OutlineButtonOpenInPopup,
        ButtonLink,
        ButtonText,
        ButtonOpenInNewTab,
        ButtonOpenInPopup,
        Tagline = '',
        Body = '',
        ColorScheme = 'primary',
        GradientDirection = 'down',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        AnimationGroup = 'content',
        Duration = 0.6,
        EditorPreview = false,
        BackgroundImage,
        BackgroundFixed = true,
        FeatureIcon = 'stars',
        FeatureIconPack = '',
        FeatureIconStyle = '',
        FeatureIconSize = 'default',
        FeatureIconColor = '',
        FeatureIconSecondaryColor = '',
        FeatureIconGradientAngle = 135,
        Icon, // legacy
        DataJson = 'reviewhero',
        DataLayout,
    } = props;

    const {
        backgroundColor,
        textColor,
        headerColor,
        taglineColor,
        iconColor: starColor,
        secondaryButton,
        thirdButton,
        overlayColor,
        backgroundStyle,
        overlayStyle,
    } = getColorScheme(ColorScheme, GradientDirection);

    const featureIconColor = String(valOf(FeatureIconColor, '') || '').trim();
    const featureIconSecondaryColor = String(
        valOf(FeatureIconSecondaryColor, '') || ''
    ).trim();
    const featureIconGradientAngle = Math.max(
        0,
        Math.min(360, Number(valOf(FeatureIconGradientAngle, 135)) || 135)
    );
    const featureIconInlineStyle = featureIconColor
        ? featureIconSecondaryColor
            ? {
                  '--review-feature-icon-gradient': `linear-gradient(${featureIconGradientAngle}deg, ${featureIconColor}, ${featureIconSecondaryColor})`,
                  color: 'transparent',
              }
            : { color: featureIconColor }
        : undefined;
    const featureIconGradientClass =
        featureIconColor && featureIconSecondaryColor
            ? 'review-feature-icon-gradient'
            : '';
    const iconColor = featureIconColor ? '' : starColor;
    const featureIconSizeClass = getFeatureIconSizeClass(FeatureIconSize);
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const { pack: sitePack, style: siteStyle } = useSiteIconOverride();
    const effectivePack =
        FeatureIconPack && FeatureIconPack.trim().length
            ? FeatureIconPack.trim()
            : sitePack;
    const effectiveStyle =
        FeatureIconStyle && FeatureIconStyle.trim().length
            ? FeatureIconStyle.trim()
            : siteStyle;
    const featureIcon =
        String(FeatureIcon || 'stars').toLowerCase() === 'quote'
            ? 'quote'
            : 'stars';
    const legacyIconClass =
        typeof Icon === 'string' && Icon.trim().length ? Icon.trim() : '';
    const featureIconRenderKey = `${effectivePack}-${effectiveStyle}-${
        legacyIconClass || featureIcon
    }`;
    const legacyIconIsStar = /\bfa-star\b/.test(legacyIconClass);
    const featureIconCount =
        legacyIconClass && !legacyIconIsStar
            ? 1
            : featureIcon === 'stars'
            ? 5
            : 1;

    /* -------------------------- Animation -------------------------- */
    const preset = normalizeAnimation(Animation);
    const requestedGroup = mapGroup(AnimationGroup);
    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const hasFeatureIcon = Boolean(
        featureIcon || Icon || FeatureIconPack || FeatureIconStyle
    );
    const hasNewOutlineCTA = hasText(OutlineButtonText);
    const resolvedOutlineText = hasNewOutlineCTA
        ? OutlineButtonText
        : ButtonText;
    const resolvedOutlineLink = hasNewOutlineCTA
        ? OutlineButtonLink
        : ButtonLink;
    const resolvedOutlineOpenInNewTab = hasNewOutlineCTA
        ? OutlineButtonOpenInNewTab
        : ButtonOpenInNewTab;
    const resolvedOutlineOpenInPopup = hasNewOutlineCTA
        ? OutlineButtonOpenInPopup
        : ButtonOpenInPopup;
    const hasCTA =
        hasText(CTA) ||
        hasText(SecondaryButtonText) ||
        hasText(resolvedOutlineText);
    const group =
        requestedGroup === 'ctas' && !hasCTA
            ? 'content'
            : requestedGroup === 'features' && !hasFeatureIcon
            ? 'content'
            : requestedGroup;

    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const animateText = group === 'content';
    const animateFeatures = group === 'features' || group === 'content';
    const animateCTAs = group === 'ctas';

    const baseKey = `review-hero-${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};

        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }

        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';

        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    const Review =
        Array.isArray(Reviews) && Reviews.length > 0 ? Reviews[0] : null;
    if (!Review) return null;

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText: resolvedOutlineText,
            OutlineButtonLink: resolvedOutlineLink,
            OutlineButtonOpenInNewTab: resolvedOutlineOpenInNewTab,
            OutlineButtonOpenInPopup: resolvedOutlineOpenInPopup,
        },
        {
            primaryVariant: 'primary',
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            enableAnim: animateCTAs && enableAnim,
            motionProps: childMotionProps,
        }
    );

    const hasBackgroundImage =
        typeof BackgroundImage === 'string' &&
        BackgroundImage.trim().length > 0;
    const shouldFixBackground =
        typeof BackgroundFixed === 'string'
            ? BackgroundFixed.toLowerCase() === 'true'
            : !!BackgroundFixed;

    return (
        <section
            className={`editable-component relative overflow-hidden ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={
                isGradientColorScheme(ColorScheme) ? backgroundStyle : undefined
            }
            data-json={DataJson}
            data-layout={DataLayout || undefined}
        >
            {hasBackgroundImage && (
                <div className="absolute inset-0 z-0">
                    <div
                        className={`absolute inset-0 h-full w-full bg-cover bg-center opacity-60 ${
                            shouldFixBackground ? 'bg-scroll lg:bg-fixed' : ''
                        }`}
                        style={{ backgroundImage: `url(${BackgroundImage})` }}
                    />
                    <div
                        className={`absolute inset-0 ${
                            overlayColor || 'bg-black/40'
                        }`}
                        style={
                            isGradientColorScheme(ColorScheme)
                                ? overlayStyle
                                : undefined
                        }
                    />
                </div>
            )}

            <div className="relative z-10 mx-auto max-w-2xl lg:max-w-4xl">
                {featureIconGradientClass && (
                    <style>{`.review-feature-icon-gradient::before, .review-feature-icon-gradient::after { display: inline-block; box-sizing: content-box; padding-block: 0.125em; margin-block: -0.125em; line-height: 1.35; background-image: var(--review-feature-icon-gradient); background-repeat: no-repeat; background-size: 100% 100%; -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }`}</style>
                )}
                {/* Header */}
                <div className="text-center">
                    {hasText(Tagline) && (
                        <MotionMaybe
                            enable={animateText && enableAnim}
                            k={`${baseKey}-tagline`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                variant="h2"
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {Tagline}
                            </MarkDown>
                        </MotionMaybe>
                    )}
                    <MotionMaybe
                        enable={animateText && enableAnim}
                        k={`${baseKey}-header`}
                        motionProps={childMotionProps}
                    >
                        <MarkDown
                            variant="h2"
                            className={`${
                                hasText(Tagline) ? 'mt-2' : ''
                            } ${headerColor} font-display text-2xl tracking-tight sm:text-3xl sm:leading-tight lg:text-4xl`}
                        >
                            {Header}
                        </MarkDown>
                    </MotionMaybe>
                    {hasText(Body) && (
                        <MotionMaybe
                            enable={animateText && enableAnim}
                            k={`${baseKey}-body`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`mt-4 text-base leading-7 ${textColor} sm:text-lg`}
                            >
                                {Body}
                            </MarkDown>
                        </MotionMaybe>
                    )}
                </div>

                {/* Feature icon */}
                <MotionMaybe
                    enable={animateFeatures && isStagger && enableAnim}
                    k={`${baseKey}-stars-container`}
                    motionProps={containerMotionProps}
                    className="mt-5 flex justify-center"
                >
                    {featureIcon === 'quote' ? (
                        <MotionMaybe
                            key={`quote-${featureIconRenderKey}`}
                            enable={animateFeatures && enableAnim}
                            k={`${baseKey}-quote-icon-${featureIconRenderKey}`}
                            motionProps={childMotionProps}
                            className={
                                featureIconSizeClass ||
                                'text-2xl sm:text-3xl lg:text-4xl'
                            }
                        >
                            <i
                                className={`${buildIconClass(
                                    'quote-left',
                                    effectivePack,
                                    effectiveStyle
                                )} ${iconColor} ${featureIconGradientClass}`}
                                aria-hidden="true"
                                style={featureIconInlineStyle}
                            />
                        </MotionMaybe>
                    ) : (
                        Array.from({ length: featureIconCount }).map((_, i) => (
                            <MotionMaybe
                                key={`star-${i}-${featureIconRenderKey}`}
                                enable={animateFeatures && enableAnim}
                                k={`${baseKey}-star-${i}-${featureIconRenderKey}`}
                                motionProps={childMotionProps}
                                className={`mx-1 ${
                                    featureIconSizeClass ||
                                    'sm:text-lg lg:text-xl'
                                }`}
                            >
                                {legacyIconClass ? (
                                    <i
                                        className={`${legacyIconClass} ${iconColor} ${featureIconGradientClass}`}
                                        aria-hidden="true"
                                        style={featureIconInlineStyle}
                                    />
                                ) : (
                                    <i
                                        className={`${buildIconClass(
                                            'star',
                                            effectivePack,
                                            effectiveStyle
                                        )} ${iconColor} ${featureIconGradientClass}`}
                                        aria-hidden="true"
                                        style={featureIconInlineStyle}
                                    />
                                )}
                            </MotionMaybe>
                        ))
                    )}
                </MotionMaybe>

                {/*Bold Review*/}
                <figure className="mt-10 items-center justify-center text-pretty">
                    <MotionMaybe
                        enable={animateText && enableAnim}
                        k={`${baseKey}-quote`}
                        motionProps={childMotionProps}
                        className={`${textColor} text-center font-text leading-8 sm:text-lg sm:leading-9 lg:text-xl`}
                    >
                        <blockquote>
                            <MarkDown className="before:content-['“'] after:content-['”'] [&>p]:inline">
                                {Review.Body}
                            </MarkDown>
                        </blockquote>
                    </MotionMaybe>

                    {/*Name and Source*/}
                    <MotionMaybe
                        enable={animateText && enableAnim}
                        k={`${baseKey}-author`}
                        motionProps={childMotionProps}
                        className="mt-10"
                    >
                        <div className="items_center mt-4 flex items-center justify-center space-x-3 text-base">
                            {!!Review.Author?.Icon && (
                                <img
                                    className="h-8 w-8 rounded-full bg-neutral-50 p-0.5 ring-1 ring-white"
                                    src={normalizeReviewSourceIcon(
                                        Review.Author.Icon
                                    )}
                                    alt={getReviewSourceAlt(Review.Author.Icon)}
                                />
                            )}
                            <MarkDown className={`${textColor}`}>
                                {Review.Author?.Name}
                            </MarkDown>
                        </div>
                    </MotionMaybe>
                </figure>

                {/* CTA */}
                {hasCTAs && CTAs}
            </div>
        </section>
    );
}
